import { useState } from 'react';
import { useNavigate, useLocation } from 'react-router';
import { useCollections, useCreateCollection } from '../../hooks/useCollections';
import { useRecipes } from '../../hooks/useRecipes';
import { useFilterStore } from '../../stores/filterStore';
import { Button } from '../ui';
import { CollectionModal } from '../collection';

export function Sidebar() {
  const navigate = useNavigate();
  const location = useLocation();
  const { data: collections = [] } = useCollections();
  const { data: recipes = [] } = useRecipes();
  const { viewMode, setViewMode } = useFilterStore();

  // Calculate counts
  const totalRecipes = recipes.length;
  const favoritesCount = recipes.filter(r => r.is_favorite).length;
  const quickMealsCount = recipes.filter(r => (r.prep_time_minutes || 0) + (r.cook_time_minutes || 0) < 30).length;

  const [showCollectionModal, setShowCollectionModal] = useState(false);
  const createCollection = useCreateCollection();

  const handleViewChange = (mode: string) => {
    setViewMode(mode);
    // Navigate to dashboard if not already there
    if (location.pathname !== '/dashboard') {
      navigate('/dashboard');
    }
  };

  const handleCreateCollection = (data: { name: string; color: string }) => {
    createCollection.mutate(data, {
      onSuccess: () => setShowCollectionModal(false),
    });
  };

  return (
    <aside className="sidebar">
      <div className="sidebar-section">
        <h3 className="sidebar-title">Quick Access</h3>
        <ul className="sidebar-nav">
          <li className="sidebar-nav-item">
            <button
              type="button"
              className={`sidebar-nav-link ${viewMode === 'all' ? 'active' : ''}`}
              onClick={() => handleViewChange('all')}
            >
              <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <path d="M3 9l9-7 9 7v11a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2z"></path>
                <polyline points="9 22 9 12 15 12 15 22"></polyline>
              </svg>
              All Recipes
              <span className="sidebar-nav-count">{totalRecipes}</span>
            </button>
          </li>
          <li className="sidebar-nav-item">
            <button
              type="button"
              className={`sidebar-nav-link ${viewMode === 'favorites' ? 'active' : ''}`}
              onClick={() => handleViewChange('favorites')}
            >
              <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <path d="M20.84 4.61a5.5 5.5 0 0 0-7.78 0L12 5.67l-1.06-1.06a5.5 5.5 0 0 0-7.78 7.78l1.06 1.06L12 21.23l7.78-7.78 1.06-1.06a5.5 5.5 0 0 0 0-7.78z"></path>
              </svg>
              Favorites
              <span className="sidebar-nav-count">{favoritesCount}</span>
            </button>
          </li>
          <li className="sidebar-nav-item">
            <button
              type="button"
              className={`sidebar-nav-link ${viewMode === 'quick-meals' ? 'active' : ''}`}
              onClick={() => handleViewChange('quick-meals')}
            >
              <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <circle cx="12" cy="12" r="10"></circle>
                <polyline points="12 6 12 12 16 14"></polyline>
              </svg>
              Quick Meals
              <span className="sidebar-nav-count">{quickMealsCount}</span>
            </button>
          </li>
        </ul>
      </div>

      <div className="sidebar-section">
        <h3 className="sidebar-title">Collections</h3>
        <ul className="sidebar-nav">
          {collections.map(collection => (
            <li key={collection.id} className="sidebar-nav-item">
              <button
                type="button"
                className={`sidebar-nav-link ${viewMode === collection.id ? 'active' : ''}`}
                onClick={() => handleViewChange(collection.id)}
              >
                <span style={{ color: collection.color || '#C45C26' }}>&#9679;</span>
                {collection.name}
                <span className="sidebar-nav-count">{collection.recipe_count || 0}</span>
              </button>
            </li>
          ))}
        </ul>
        <Button
          variant="ghost"
          size="sm"
          onClick={() => setShowCollectionModal(true)}
          style={{
            width: '100%',
            marginTop: 'var(--spacing-sm)',
            justifyContent: 'flex-start',
          }}
        >
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            <line x1="12" y1="5" x2="12" y2="19"></line>
            <line x1="5" y1="12" x2="19" y2="12"></line>
          </svg>
          Add Collection
        </Button>
      </div>

      <CollectionModal
        isOpen={showCollectionModal}
        onClose={() => setShowCollectionModal(false)}
        onSave={handleCreateCollection}
        isLoading={createCollection.isPending}
      />
    </aside>
  );
}
